// @givmo/board — EVERY word CBG-RC9's screens add, in one place: directors approve minutes by written
// consent (D-CBG-22).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the RC9 mockups for building on 2026-10-07 ("Approved"; Q7 "Yes",
// Q8 "Warn") with every word kept a draft, and then the words themselves in copy round CBG-RC9-COPY1
// on 2026-10-12 13:49 PDT ("otherwise approved"): every line here, as written, with the page's one
// suggestion (a meeting's title keeps its own capital). Each line names the frame that draws it, or
// "not drawn" for a state no frame shows. The minutes page's door, `approveByConsent`, waited for
// CBG-RC11's design, whose approved frames keep it as written consent's one action (the
// orchestrator's C16), so it is approved too.
//
// One line moved since: `completedByUnnamed`'s fallback is CBG-FIX49's (C13), approved on the RC11 copy
// page beside the form it replaces (CBG-RC11-COPY1, 2026-10-13 21:51 PDT).
//
// ⚠ Plan §3.2 and §3.4 bind every line: what Board recorded and what a signature does, never a
// conclusion about the minutes or the consent. No pronoun for a named person. A title is shown as the
// organization wrote it, never re-cased (the suggested consent title's own rule, 2026-10-06).

import { displayDate, displayLongDate } from '../../lib/display';
import { oneLine } from '../conflicts/coi-decode';

/** How a helper naming minutes says their meeting's date. */
export interface MinutesNaming {
  /**
   * Keep the date on one line wherever the sentence wraps (`oneLine`), for words a page only shows.
   * Never for words that become stored text: the motion's prefilled wording is content-addressed at
   * its vote, so it keeps plain spaces.
   */
  keepDateTogether?: boolean;
}

/** The meeting's date written out, "September 24, 2026", as `naming` says; null when unreadable. */
export function namedMeetingDate(
  meetingDate: string | null | undefined,
  naming: MinutesNaming = {},
): string | null {
  const date = displayLongDate(meetingDate);
  return date !== null && naming.keepDateTogether === true ? oneLine(date) : date;
}

/**
 * Which minutes, in a sentence: "the minutes of the Annual board meeting of September 24, 2026". The
 * meeting's title as the organization wrote it, and its date written out, which tells two meetings of
 * one name apart.
 */
export function minutesOfTheMeeting(
  meetingTitle: string | null | undefined,
  meetingDate: string | null | undefined,
  naming: MinutesNaming = {},
): string {
  const title = (meetingTitle ?? '').trim();
  const date = namedMeetingDate(meetingDate, naming);
  if (title.length > 0 && date !== null) {
    // Drawn, rc9-circulate and rc9-director ("the annual board meeting of September 24, 2026"; the
    // title keeps its own capital).
    return `the minutes of the ${title} of ${date}`;
  }
  if (date !== null) {
    // Not drawn: the meeting's title isn't known.
    return `the minutes of the meeting of ${date}`;
  }
  // Not drawn: neither the title nor the date is known.
  return 'the minutes it names';
}

/** The minutes, named on their own: "The minutes of the Annual board meeting, September 24, 2026". */
export function theMinutesOf(
  meetingTitle: string | null | undefined,
  meetingDate: string | null | undefined,
  naming: MinutesNaming = {},
): string {
  const title = (meetingTitle ?? '').trim();
  const date = namedMeetingDate(meetingDate, naming);
  if (title.length > 0 && date !== null) {
    // Drawn, rc9-consent-draft ("Approves").
    return `The minutes of the ${title}, ${date}`;
  }
  if (title.length > 0) {
    // Not drawn: the minutes have no date yet.
    return `The minutes of the ${title}`;
  }
  // Not drawn: the meeting's title isn't known.
  return date !== null ? `The minutes of the meeting of ${date}` : 'These minutes';
}

export const minutesConsentWords = {
  // ── The minutes page ────────────────────────────────────────────────────────────────────────────
  // Drawn, rc9-minutes-action (the plan line's words), and RC11's m1 (written consent's one action).
  approveByConsent: 'Approve by written consent',
  // Drawn, rc9-minutes-pending.
  pendingLine: 'A written consent approving these minutes is out for signature.',
  // Drawn, rc9-minutes-pending (Q8's warning).
  pendingNote: 'If these minutes change before the last signature, the consent won’t approve them.',
  // Drawn, rc9-minutes-pending, -approved and -refused.
  openConsent: 'Open the consent',
  // Not drawn: a consent naming these minutes is a draft, not yet out.
  draftedLine:
    'A written consent approving these minutes is drafted, and hasn’t gone to the board.',
  // Drawn, rc9-minutes-approved (the plan line's lead).
  approvedLine: (approvedAt: string | null | undefined) =>
    `Approved by written consent of the directors on ${displayDate(approvedAt)}.`,
  refusedLine: (outcome: string | null) => {
    switch (outcome) {
      case 'minutes_changed':
        // Drawn, rc9-minutes-refused.
        return 'The written consent for these minutes didn’t approve them: they changed after it went out to the board.';
      case 'meeting_record_changed':
        // Not drawn: the record of their meeting moved on.
        return 'The written consent for these minutes didn’t approve them: the record of their meeting changed after they were drafted.';
      default:
        // Not drawn: an outcome this page doesn't know.
        return 'The written consent for these minutes didn’t approve them.';
    }
  },

  // ── Starting the consent (the dialog over the minutes) ──────────────────────────────────────────
  // Drawn, rc9-consent-draft.
  dialogTitle: 'Approve these minutes by written consent',
  // Drawn, rc9-consent-draft.
  dialogLead:
    'Start a written consent for your directors to sign. When the last director signs, these minutes are approved, as they are when the consent goes out.',
  // Drawn, rc9-consent-draft.
  approves: 'Approves',
  // Drawn, rc9-consent-draft-text (the one enforced line, Q7).
  filledInForYou:
    'Givmo fills in which minutes, from your records: their title, date, record and fingerprint. Keep the fingerprint as it is — it’s how the signatures name these exact minutes, and the consent can’t go out to the board without it. The terms in brackets are yours to fill in.',
  // Drawn, rc9-consent-draft-text.
  stillInBrackets: (count: number) =>
    `${count} ${count === 1 ? 'field is' : 'fields are'} still in brackets. You can save now and finish later — nothing goes to your board until you put it to the board.`,
  // Not drawn: the form couldn't be read.
  formUnavailable: 'We couldn’t open the form just now.',

  // ── Putting it to the board ─────────────────────────────────────────────────────────────────────
  // Drawn, rc9-circulate.
  circulateLead: (minutes: string) =>
    `Signing it approves ${minutes}, named in its text by their fingerprint.`,
  // Drawn, rc9-circulate.
  circulateIfChanged:
    'They’re approved as they are now. If they change before the last signature, the consent is still filed, and the minutes aren’t approved.',

  // ── The consent's own page (officer) ────────────────────────────────────────────────────────────
  // Drawn, rc9-circulate and rc9-consent-refused.
  whatItApproves: 'What it approves',
  // Drawn, rc9-consent-refused.
  openMinutes: 'Open the minutes',
  // Drawn, rc9-consent-refused.
  namedByFingerprint: 'Named in its text by this fingerprint',
  // Drawn, rc9-circulate.
  pinnedWhenOut: 'Pinned by their fingerprint when you put it to the board.',
  // Not drawn: a copy button's accessible name, never shown.
  minutesFingerprintLabel: 'the minutes it approves',
  // Not drawn: a copy button's accessible name, never shown.
  wordingFingerprintLabel: 'this consent’s wording',
  // Not drawn: the minutes it names are gone (discarded or removed).
  minutesGone: 'The minutes it names are no longer in your minute book.',
  // Not drawn: executed, and it approved them.
  approvedThem: 'It approved them when the last signature was recorded.',
  // Not drawn: executed after they were approved another way.
  alreadyApproved:
    'They were approved another way before the last signature, so this consent didn’t approve them. It’s filed as signed.',
  // Drawn, rc9-consent-refused.
  refusedTitle: 'These minutes weren’t approved',
  refusedWhy: (outcome: string | null, served: string | null) => {
    switch (outcome) {
      case 'minutes_changed':
        // Drawn, rc9-consent-refused.
        return 'They changed after this consent went out. The consent is filed as signed.';
      case 'meeting_record_changed':
        // Not drawn.
        return 'The record of their meeting changed after they were drafted. The consent is filed as signed.';
      case 'minutes_unavailable':
        // Not drawn.
        return 'They were no longer in your minute book when the last signature was recorded. The consent is filed as signed.';
      default:
        // Not drawn: an outcome this page doesn't know shows the backend's own sentence, or this.
        return served ?? 'The consent is filed as signed.';
    }
  },
  refusedNext: (outcome: string | null): string | null => {
    switch (outcome) {
      case 'minutes_changed':
        // Drawn, rc9-consent-refused.
        return 'Approve the current minutes another way, or start a new consent for them.';
      case 'meeting_record_changed':
        // Not drawn.
        return 'Refresh them from what’s recorded, then approve them another way or start a new consent for them.';
      default:
        return null;
    }
  },

  // ── The director's page ─────────────────────────────────────────────────────────────────────────
  // Drawn, rc9-director.
  directorLead: (minutes: string) => `Signing this approves ${minutes}.`,
  // Drawn, rc9-director.
  readMinutes: 'Read the minutes',
  wontApprove: (reason: string | null) => {
    switch (reason) {
      case 'minutes_changed':
        // Drawn, rc9-director-changed.
        return 'The minutes this consent names have changed since it went out, so it won’t approve them.';
      case 'minutes_already_approved':
        // Not drawn (the frames' README word table). Good news: said quietly.
        return 'The minutes this consent names have been approved since it went out, so signing it won’t approve them again.';
      case 'meeting_record_changed':
        // Not drawn (the frames' README word table).
        return 'The meeting’s record has changed since these minutes were drafted, so this consent won’t approve them.';
      case 'minutes_unavailable':
        // Not drawn (the frames' README word table).
        return 'The minutes this consent names can no longer be approved, so it won’t approve them.';
      default:
        // Not drawn (the README's fallback): a reason this page doesn't know.
        return 'This consent won’t approve the minutes it names.';
    }
  },
  // Drawn, rc9-director-minutes.
  sheetNote:
    'These are the minutes this consent names. They haven’t changed since it went out to the board.',
  // Drawn, rc9-director-minutes.
  secretarysSections: 'The secretary’s sections',
  // Drawn, rc9-director-minutes (the officer's minutes page's label).
  whatWasRecorded: 'What was recorded about the meeting',
  // Drawn, rc9-director-minutes.
  theirFingerprint: 'Their fingerprint',
  // Drawn, rc9-director-minutes.
  close: 'Close',
  // Not drawn: the minutes couldn't be read.
  minutesUnavailable: 'We couldn’t open the minutes just now.',

  // ── The minute book ─────────────────────────────────────────────────────────────────────────────
  // Drawn, rc9-record (CBG-UX3's rule: never one director as the approver).
  recordApproval: (when: string) =>
    `Approved by a written consent signed by every director it went out to; the last signature on ${when}`,
  // Drawn, rc9-record.
  eventTitle: 'Approved by written consent of the directors',
  // Drawn, rc9-record (a director's own signature completed it).
  completedBy: (seat: string) => `${seat}’s signature completed the consent`,
  // Not drawn: an officer recorded the completing signature for the seat.
  completedByOnBehalf: (seat: string, officer: string) =>
    `${seat}’s signature, recorded by ${officer}, completed the consent`,
  // Not drawn: the completing seat's name isn't shown (C13: was "a board member whose name isn’t
  // shown", CBG-YR3-ROLES-COPY4's approved form).
  completedByUnnamed: 'Completed by the signature of someone whose name isn’t shown',
} as const;
